import axios, { AxiosInstance, AxiosResponse } from "axios";
import { LocalCache } from "../helpers/cache";
import { NullableString } from "../core/models/ModelHelpers";
import { EVENT_FORCE_ROUTE_CHANGE } from "../Events";
import IHeaders from "../core/contracts/IHeaders";
import Paginate from "../core/models/Paginate";
import { FALLBACK_API_URL } from "../core/constants/appGlobals";

export default class BaseService {
  api: AxiosInstance;
  bearerToken: NullableString;
  baseURL: string;
  radixAPI: boolean;

  constructor(radixAPI = false, responseTypeFile = false) {
    this.radixAPI = radixAPI;
    this.bearerToken = this.getToken();
    // TODO: endereço da api local, homolog
    // this.baseURL = radixAPI ? this.getBaseURL() : "https://apitelemetria.whitemartins.com.br";
    this.baseURL = this.getBaseURL();

    const headers = {
      authorization: this.bearerToken ? `Bearer ${this.bearerToken}` : "",
      APIKey: "5misWDbG1ITBhx7io3Lb4Zob",
    };

    this.api = axios.create({
      baseURL: this.baseURL,
      headers: headers,
      responseType: responseTypeFile ? "arraybuffer" : "json",
    });
  }

  private getBaseURL(): string {
    const url = process.env?.REACT_APP_API_URL;
    return url ? url : FALLBACK_API_URL;
  }

  getToken(): NullableString {
    const sessionStorage = new LocalCache();

    const cachedBearer: NullableString = sessionStorage.get<NullableString>("bearer-token");
    return cachedBearer;
  }

  async get<T>(url: string): Promise<T> {
    try {
      const results = await this.api.get(url);
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async getPaginated<T>(url: string): Promise<Paginate<T>> {
    try {
      const results = await this.api.get(url);
      const items = this.handleResponse<T[]>(results);
      const headers = this.handleHeaders<T>(results);
      const pagination = JSON.parse(headers.pagination || "");

      const response: Paginate<T> = {
        items,
        pagination,
      };

      return response;
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async delete<T, U>(url: string, params?: U): Promise<T> {
    try {
      const results = await this.api.delete(url, { data: params });
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async put<T, U>(url: string, params?: U): Promise<T> {
    try {
      const results = await this.api.put(url, params);
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  async post<T, U>(url: string, params?: U): Promise<T> {
    try {
      const results = await this.api.post(url, params);
      return this.handleResponse<T>(results);
    } catch (error) {
      this.handleRequestError(error.response);
      return new Promise((_, rej) => {
        rej(error.response);
      });
    }
  }

  private handleResponse<T>(APIResponse: AxiosResponse): T {
    return APIResponse.data;
  }

  private handleHeaders<T>(APIResponse: AxiosResponse): IHeaders {
    return APIResponse.headers;
  }

  private clearCacheAndReroute() {
    const cache = new LocalCache();
    cache.clear();
    window?.Hubx?.notify(EVENT_FORCE_ROUTE_CHANGE, "/expired_token/");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleRequestError(APIResponse: any) {
    // eslint-disable-next-line no-console
    console.warn("BaseService -> handleRequestError ->", APIResponse);

    if (this.radixAPI && (APIResponse?.code === 401 || APIResponse?.status === 401)) {
      this.clearCacheAndReroute();
    }

    return Promise.reject(APIResponse);
  }
}
